import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../lib/apiClient';
import { useDeleteQueue } from '../tasks/DeleteQueueContext';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    Button,
    Progress,
    Skeleton,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@pagespeedify/ui';
import { toast } from 'sonner';
import {
    Download,
    Github,
    Bot,
    Upload,
    CheckCircle2,
    AlertCircle,
    Clock,
    Loader2,
    ExternalLink,
    ImageIcon,
    Edit2,
    Save,
    X,
    Trash2,
    Settings2,
    GitPullRequest,
    Tag,
    Flag,
    GitBranch,
} from 'lucide-react';
import { EditImageOptimizationTaskForm } from './EditImageOptimizationTaskForm';


interface ImageOptimization {
    id: string;
    taskId?: string;
    status: 'pending' | 'processing' | 'completed' | 'failed' | 'success' | 'resolved' | 'closed';
    inputType: 'upload' | 'github' | 'agent';
    source: {
        filename?: string;
        file_path?: string;
        repo_owner?: string;
        repo_name?: string;
    };
    originalSizeBytes: number;
    optimizedSizeBytes?: number;
    downloadUrl?: string;
    createdAt: string;
    completedAt?: string;
    error?: string;
    // New fields
    optimizationType?: string;
    outputFormat?: string;
    deletedAt?: string;
}

interface BatchStatus {
    taskId: string;
    title?: string;
    inputType: 'upload' | 'github' | 'agent';
    total: number;
    completed: number;
    failed: number;
    remaining: number;

    // Derived fields for display
    optimizationType?: string;
    outputFormat?: string;
    completedAt?: string;

    status: 'pending' | 'processing' | 'completed' | 'failed' | 'success' | 'resolved' | 'closed';
    images: ImageOptimization[];
    downloadUrl?: string;
    prUrl?: string;
    createdAt?: string;
    source?: string;
    githubRepo?: string; // Derived from response.github_repo
    priority?: string;
    tag?: string;
    githubBranch?: string;
}

// Simple badge component
const SimpleBadge = ({ className, children }: { className?: string; children: React.ReactNode }) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${className || ''}`}>
        {children}
    </span>
);

const StatusBadge = ({ status }: { status: string }) => {
    switch (status) {
        case 'completed':
        case 'resolved':
        case 'success':
            return <SimpleBadge className="bg-green-500/20 text-green-500"><CheckCircle2 className="h-3 w-3 mr-1" /> Completed</SimpleBadge>;
        case 'processing':
            return <SimpleBadge className="bg-blue-500/20 text-blue-500"><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Processing</SimpleBadge>;
        case 'pending':
            return <SimpleBadge className="bg-yellow-500/20 text-yellow-500"><Clock className="h-3 w-3 mr-1" /> Pending</SimpleBadge>;
        case 'failed':
        case 'closed':
            return <SimpleBadge className="bg-red-500/20 text-red-500"><AlertCircle className="h-3 w-3 mr-1" /> Failed</SimpleBadge>;
        default:
            return <SimpleBadge className="bg-gray-500/20 text-gray-500">{status}</SimpleBadge>;
    }
};

const formatBytes = (bytes: number): string => {
    // Handle edge cases that cause NaN
    if (bytes === null || bytes === undefined || isNaN(bytes)) return '—';
    if (bytes === 0) return '0 B';

    const sign = bytes < 0 ? '-' : '';
    const absBytes = Math.abs(bytes);

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const i = Math.floor(Math.log(absBytes) / Math.log(k));

    if (i >= sizes.length || isNaN(i)) return sign + parseFloat((absBytes / Math.pow(k, i)).toFixed(1)) + ' ?';
    return sign + parseFloat((absBytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const PriorityBadge = ({ priority }: { priority?: string }) => {
    if (!priority) return null;
    const colors: Record<string, string> = {
        high: 'bg-red-500/20 text-red-500',
        medium: 'bg-yellow-500/20 text-yellow-500',
        low: 'bg-blue-500/20 text-blue-500',
    };
    return (
        <SimpleBadge className={colors[priority.toLowerCase()] || 'bg-gray-500/20 text-gray-500'}>
            <Flag className="h-3 w-3 mr-1" />
            {priority.charAt(0).toUpperCase() + priority.slice(1)}
        </SimpleBadge>
    );
};

interface ImageOptimizationTaskViewProps {
    taskId: string;
    onClose?: () => void;
    onDelete?: (taskId: string, title?: string) => void;
}

export const ImageOptimizationTaskView: React.FC<ImageOptimizationTaskViewProps> = ({ taskId, onClose, onDelete }) => {
    const api = useApi();
    const queryClient = useQueryClient();
    const { addTaskToDeleteQueue } = useDeleteQueue();
    const [isEditing, setIsEditing] = useState(false);
    const [isGeneratingZip, setIsGeneratingZip] = useState(false);
    const [isCommitting, setIsCommitting] = useState(false);

    // Fetch batch status
    const { data: batchStatus, isLoading, isError, refetch } = useQuery<BatchStatus>({
        queryKey: ['imageBatch', taskId],
        queryFn: async () => {
            // Backend returns BatchStatusResponse with snake_case fields
            const response = await api.get<any>(`/images/batch/${taskId}`);

            // Map backend snake_case to frontend camelCase
            const mappedInputTypeRaw = response.input_type || 'upload';
            const mappedInputType = mappedInputTypeRaw === 'swayashin_agent' ? 'agent' : mappedInputTypeRaw;
            const imagesMapped: ImageOptimization[] = (response.jobs || []).map((job: any) => ({
                id: job.job_id,
                taskId: response.task_id,
                status: job.status,
                inputType: mappedInputType,
                source: {
                    filename: job.original_filename,
                    file_path: job.file_path, // Map the new file_path field
                },
                originalSizeBytes: job.original_size || 0,
                optimizedSizeBytes: job.optimized_size,
                downloadUrl: job.download_url,
                createdAt: response.created_at || new Date().toISOString(), // Fallback if missing on job
                completedAt: job.completed_at,
                optimizationType: job.optimization_type,
                outputFormat: job.output_format,
                deletedAt: job.deleted_at,
            }));

            // Derive common settings from the first image (since it's a batch)
            const firstImage = imagesMapped[0];
            const optimizationType = firstImage?.optimizationType;
            const outputFormat = firstImage?.outputFormat;

            // Find latest completion time
            const latestCompletion = imagesMapped
                .map(img => img.completedAt ? new Date(img.completedAt).getTime() : 0)
                .reduce((a, b) => Math.max(a, b), 0);
            const completedAt = latestCompletion > 0 ? new Date(latestCompletion).toISOString() : undefined;

            const mapped: BatchStatus = {
                taskId: response.task_id || taskId,
                title: response.title,
                inputType: mappedInputType,
                total: response.total || 0,
                completed: response.completed || 0,
                failed: response.failed || 0,
                remaining: (response.total || 0) - (response.completed || 0) - (response.failed || 0),
                status: response.status || 'pending',
                downloadUrl: response.download_url,
                prUrl: response.pr_url,
                createdAt: response.created_at,
                completedAt,
                source: response.source,
                githubRepo: response.github_repo, // Map github_repo
                optimizationType,
                outputFormat,
                priority: response.priority,
                tag: response.tag,
                githubBranch: response.github_branch,
                images: imagesMapped,
            };
            return mapped;
        },
        enabled: !!taskId,
        retry: 1, // Don't retry indefinitely on failure
        refetchInterval: (query) => {
            // Stop polling if there was an error
            if (query.state.status === 'error') {
                return false;
            }
            const data = query.state.data as BatchStatus | undefined;
            // Stop polling if no data (still loading) or terminal status
            if (!data || data.status === 'completed' || data.status === 'failed' || data.status === 'resolved' || data.status === 'closed') {
                return false;
            }
            return 3000; // Poll every 3 seconds while processing
        },
    });

    // Fetch usage for retention policy
    const { data: usageData } = useQuery({
        queryKey: ['imageUsage'],
        queryFn: async () => {
            return api.get<any>('/images/usage');
        }
    });

    // Fetches...

    // Removed handleUpdateTask and useEffect for edit state sync as Form handles it manually

    const handleCreatePR = async () => {
        if (isCommitting) return;
        setIsCommitting(true);
        try {
            toast.info('Creating Pull Request...', { id: 'create-pr' });

            // Call the new commit endpoint
            // The response contains { success: true, pr_url: string, branch: string, commit_sha: string }
            const response = await api.post<{ success: boolean; pr_url: string }>(`/images/github/commit/${taskId}`, {});

            if (response.success && response.pr_url) {
                toast.success('Pull Request created successfully!', { id: 'create-pr' });

                // Optimistically update the cache to show the 'View on GitHub' button immediately
                queryClient.setQueryData(['imageBatch', taskId], (oldData: BatchStatus | undefined) => {
                    if (!oldData) return oldData;
                    return {
                        ...oldData,
                        prUrl: response.pr_url
                    };
                });
            }

            // Refetch to ensure full sync
            refetch();
        } catch (error: any) {
            console.error('Failed to create PR', error);
            const msg = error.response?.data?.message || error.message || 'Failed to create Pull Request';
            toast.error(msg, { id: 'create-pr' });
        } finally {
            setIsCommitting(false);
        }
    };

    if (isLoading) {
        return (
            <Card>
                <CardHeader>
                    <Skeleton className="h-6 w-48" />
                </CardHeader>
                <CardContent>
                    <Skeleton className="h-32 w-full" />
                </CardContent>
            </Card>
        );
    }

    if (isError || !batchStatus) {
        return (
            <Card>
                <CardContent className="py-12 text-center">
                    <AlertCircle className="h-8 w-8 mx-auto text-red-500 mb-2" />
                    <p className="text-muted-foreground mb-4">Failed to load optimization details</p>
                    {onDelete && (
                        <Button variant="destructive" size="sm" onClick={() => onDelete(taskId, 'Unknown Task')}>
                            Delete Task
                        </Button>
                    )}
                </CardContent>
            </Card>
        );
    }

    if (isEditing) {
        return (
            <EditImageOptimizationTaskForm
                taskId={taskId}
                initialTitle={batchStatus.title || (batchStatus.inputType === 'agent'
                    ? 'Agent Optimization'
                    : (batchStatus.inputType === 'github' ? 'GitHub Optimization' : 'Upload Optimization'))}
                initialTag={batchStatus.tag}
                initialPriority={batchStatus.priority}
                onClose={() => setIsEditing(false)}
                onSuccess={() => {
                    setIsEditing(false);
                    refetch();
                }}
            />
        );
    }

    const progressPercent = batchStatus.total > 0
        ? Math.round(((batchStatus.completed + batchStatus.failed) / batchStatus.total) * 100)
        : 0;
    const isAgentSource = batchStatus.inputType === 'agent';
    const isGithubSource = batchStatus.inputType === 'github';
    const sourceLabel = isAgentSource
        ? 'Agent'
        : (isGithubSource ? 'GitHub Repository' : 'Direct Upload');
    const defaultTitle = isAgentSource
        ? 'Agent Optimization'
        : (isGithubSource ? 'GitHub Optimization' : 'Upload Optimization');

    const totalOriginalSize = batchStatus.images.reduce((sum, img) => sum + Number(img.originalSizeBytes || 0), 0);
    const totalOptimizedSize = batchStatus.images
        .filter(img => img.status === 'completed' || img.status === 'success')
        .reduce((sum, img) => sum + Number(img.optimizedSizeBytes || 0), 0);
    const savedBytes = totalOriginalSize - totalOptimizedSize;
    const savedPercent = totalOriginalSize > 0 ? Math.round((savedBytes / totalOriginalSize) * 100) : 0;

    // Calculate expiration
    const validityDays = usageData?.usage?.accessContext?.planFeatures?.validity?.image_optimizations;
    const expirationDate = validityDays && batchStatus?.createdAt
        ? new Date(new Date(batchStatus.createdAt).getTime() + validityDays * 24 * 60 * 60 * 1000)
        : null;

    const daysUntilExpiration = expirationDate
        ? Math.ceil((expirationDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
        : null;

    return (
        <div className="space-y-4">
            {/* Retention Warning */}


            {/* Header with status */}
            <Card>
                <CardHeader className="pb-3 px-4 sm:px-6">
                    <div className="flex flex-col gap-3">
                        {/* Left Side: Icon + Title + Meta Info */}
                        <div className="flex items-start gap-2.5 w-full">
                            {isAgentSource ? (
                                <Bot className="hidden sm:block h-8 w-8 text-muted-foreground shrink-0 mt-1" />
                            ) : isGithubSource ? (
                                <Github className="hidden sm:block h-8 w-8 text-muted-foreground shrink-0 mt-1" />
                            ) : (
                                <Upload className="hidden sm:block h-8 w-8 text-muted-foreground shrink-0 mt-1" />
                            )}

                            <div className="flex flex-col gap-3 min-w-0 w-full">
                                {/* Row 1: Title (Full text, no truncation) + Actions */}
                                <div className="flex justify-between items-start gap-4">
                                    <CardTitle className="text-xl leading-snug break-words">
                                        {/* Mobile Icon Inline */}
                                        <span className="inline-block sm:hidden mr-2 align-middle relative -top-0.5">
                                            {isAgentSource ? (
                                                <Bot className="h-5 w-5 text-muted-foreground" />
                                            ) : isGithubSource ? (
                                                <Github className="h-5 w-5 text-muted-foreground" />
                                            ) : (
                                                <Upload className="h-5 w-5 text-muted-foreground" />
                                            )}
                                        </span>
                                        {batchStatus.title || defaultTitle}
                                    </CardTitle>

                                    {/* Action Buttons */}
                                    <div className="flex items-center gap-1 shrink-0">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 w-8 p-0"
                                            onClick={() => setIsEditing(true)}
                                            title="Edit Task"
                                        >
                                            <Edit2 className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                                        </Button>

                                        {onDelete && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-8 w-8 p-0 hover:bg-accent text-destructive hover:text-destructive"
                                                onClick={() => {
                                                    if (onDelete) {
                                                        onDelete(taskId, batchStatus.title);
                                                    } else {
                                                        addTaskToDeleteQueue({
                                                            id: taskId,
                                                            title: batchStatus.title || 'Image Optimization',
                                                            type: 'image_optimization',
                                                            status: batchStatus.status,
                                                            created_at: batchStatus.createdAt || new Date().toISOString(),
                                                        } as any);
                                                    }
                                                }}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        )}
                                    </div>
                                </div>

                                {/* Combined Meta Row for Linear Desktop Layout */}
                                <div className="flex flex-col gap-y-2 text-sm text-muted-foreground md:flex-row md:flex-wrap md:items-center md:gap-x-6">
                                    {/* Source + Branch */}
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                                        <span className="flex items-center gap-1.5 min-w-fit">
                                            <span className="text-muted-foreground/70">Source:</span>
                                            <span className="font-medium text-foreground">
                                                {sourceLabel}
                                            </span>
                                        </span>

                                        {batchStatus.githubRepo && (
                                            <span className="flex items-center gap-1.5 min-w-fit">
                                                <Github className="h-3.5 w-3.5 text-muted-foreground/70" />
                                                <span className="font-medium text-foreground">{batchStatus.githubRepo}</span>
                                            </span>
                                        )}

                                        {batchStatus.githubBranch && (
                                            <span className="flex items-center gap-1.5 min-w-fit">
                                                <GitBranch className="h-3.5 w-3.5 text-muted-foreground/70" />
                                                <span className="font-medium text-foreground">{batchStatus.githubBranch}</span>
                                            </span>
                                        )}
                                    </div>

                                    {/* Usage + Expiry */}
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                                        {usageData?.usage && (
                                            <div className="flex items-center gap-1.5 min-w-fit">
                                                <Settings2 className="h-3.5 w-3.5 text-muted-foreground/70" />
                                                <span>
                                                    Usage: <span className="font-medium text-foreground">{usageData.usage.consumedInPeriod}</span> / {usageData.usage.limitForPeriod}
                                                </span>
                                            </div>
                                        )}

                                        {expirationDate && (
                                            <div className="flex items-center gap-1.5 min-w-fit text-destructive">
                                                <Clock className="h-3.5 w-3.5 text-destructive" />
                                                <span>
                                                    {daysUntilExpiration !== null && daysUntilExpiration >= 0 && (
                                                        <span className="text-destructive font-medium whitespace-nowrap bg-destructive/10 px-1.5 py-0.5 rounded">
                                                            {daysUntilExpiration === 0 ? 'Files expire today' : `Files expire in ${daysUntilExpiration} days`}
                                                        </span>
                                                    )}
                                                    {daysUntilExpiration !== null && daysUntilExpiration < 0 && (
                                                        <span className="text-destructive font-medium whitespace-nowrap bg-destructive/10 px-1.5 py-0.5 rounded">
                                                            Files expired
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Badges */}
                                    <div className="flex flex-wrap items-center gap-4">
                                        <div className="flex items-center gap-2">
                                            <span className="hidden md:inline text-muted-foreground/70 text-xs">Format:</span>
                                            <span className="text-xs font-semibold bg-muted/50 border border-border px-2 py-1 rounded shadow-sm text-foreground uppercase tracking-tight">
                                                {batchStatus.outputFormat || 'N/A'}
                                            </span>
                                            <span className="hidden md:inline text-muted-foreground/70 text-xs ml-2">Type:</span>
                                            <span className="text-xs font-semibold bg-muted/50 border border-border px-2 py-1 rounded shadow-sm text-foreground capitalize tracking-tight">
                                                {batchStatus.optimizationType || 'N/A'}
                                            </span>
                                        </div>

                                        {batchStatus.tag && (
                                            <SimpleBadge className="bg-primary/10 text-primary border border-primary/20">
                                                <Tag className="h-3 w-3 mr-1" />
                                                {batchStatus.tag}
                                            </SimpleBadge>
                                        )}

                                        <div className="flex items-center gap-1">
                                            <span className="hidden md:inline text-muted-foreground/70 text-xs">Priority:</span>
                                            <PriorityBadge priority={batchStatus.priority} />
                                        </div>

                                        <div className="flex items-center gap-1">
                                            <span className="hidden md:inline text-muted-foreground/70 text-xs">Status:</span>
                                            <StatusBadge status={batchStatus.status} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {/* Progress bar and Download Button */}
                    <div className="mb-6 flex flex-col md:flex-row md:items-end gap-4">
                        <div className="space-y-2 flex-1 w-full">
                            <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground font-medium">Optimization Progress</span>
                                <span className="font-medium">{progressPercent}%</span>
                            </div>
                            <Progress value={progressPercent} className="h-2" />
                            <div className="flex justify-between text-xs text-muted-foreground">
                                <span>{batchStatus.completed} completed</span>
                                {batchStatus.failed > 0 && (
                                    <span className="text-red-500">{batchStatus.failed} failed</span>
                                )}
                                <span className="font-mono text-muted-foreground/80">ID: {taskId.slice(0, 8)}</span>
                            </div>
                        </div>

                        {/* Action Buttons Column */}
                        <div className="flex flex-col gap-2 w-full md:w-auto">
                            <div className="grid grid-cols-1 sm:flex items-center gap-2 w-full">
                                {/* Download Button */}
                                {(batchStatus.status === 'completed' || batchStatus.status === 'success' || batchStatus.status === 'resolved') &&
                                    (batchStatus.downloadUrl || batchStatus.images.some(img => img.downloadUrl) || batchStatus.images.some(img => img.deletedAt)) && (
                                        <Button
                                            variant="default"
                                            className="shrink-0"
                                            disabled={isGeneratingZip || batchStatus.images.some(img => img.deletedAt)}
                                            onClick={async () => {
                                                if (batchStatus.images.some(img => img.deletedAt)) return;

                                                if (batchStatus.downloadUrl) {
                                                    const link = document.createElement('a');
                                                    link.href = batchStatus.downloadUrl;
                                                    link.download = '';
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                } else {
                                                    try {
                                                        setIsGeneratingZip(true);
                                                        toast.info('Generating ZIP...', { id: 'zip-gen' });
                                                        const res = await api.get<{ success: boolean; download_url: string }>(`/images/batch/${taskId}/zip`);

                                                        if (res.success && res.download_url) {
                                                            toast.success('ZIP ready!', { id: 'zip-gen' });
                                                            const link = document.createElement('a');
                                                            link.href = res.download_url;
                                                            link.download = '';
                                                            document.body.appendChild(link);
                                                            link.click();
                                                            document.body.removeChild(link);
                                                        } else {
                                                            toast.error('Failed to generate ZIP', { id: 'zip-gen' });
                                                        }
                                                    } catch (e) {
                                                        console.error(e);
                                                        toast.error('Failed to generate ZIP', { id: 'zip-gen' });
                                                    } finally {
                                                        setIsGeneratingZip(false);
                                                    }
                                                }
                                            }}
                                        >
                                            {isGeneratingZip ? (
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            ) : (
                                                <Download className="h-4 w-4 mr-2" />
                                            )}
                                            {isGeneratingZip ? 'Zipping...' : (batchStatus.images.some(img => img.deletedAt) ? 'Expired' : 'Download All')}
                                        </Button>
                                    )}

                                {/* Save to GitHub Button */}
                                {batchStatus.inputType === 'github' &&
                                    (batchStatus.status === 'completed' || batchStatus.status === 'success') &&
                                    !batchStatus.prUrl && (
                                        <Button
                                            variant="default"
                                            className="shrink-0 bg-[#24292F] hover:bg-[#24292F]/90 text-white"
                                            onClick={handleCreatePR}
                                            disabled={isCommitting}
                                        >
                                            {isCommitting ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                    Saving...
                                                </>
                                            ) : (
                                                <>
                                                    <GitPullRequest className="h-4 w-4 mr-2" />
                                                    Save to GitHub
                                                </>
                                            )}
                                        </Button>
                                    )}

                                {/* View PR Button */}
                                {batchStatus.prUrl && (
                                    <Button asChild variant="default" className="shrink-0 bg-[#24292F] hover:bg-[#24292F]/90 text-white">
                                        <a href={batchStatus.prUrl} target="_blank" rel="noopener noreferrer">
                                            <ExternalLink className="h-4 w-4 mr-2" />
                                            View on GitHub
                                        </a>
                                    </Button>
                                )}
                            </div>


                        </div>
                    </div>

                    {/* Stats */}
                    {(batchStatus.status === 'completed' || batchStatus.status === 'resolved') && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                            <div className="p-4 bg-muted/30 rounded-lg text-center">
                                <p className="text-2xl font-bold">{batchStatus.total}</p>
                                <p className="text-xs text-muted-foreground uppercase tracking-wider">Images</p>
                            </div>
                            <div className="p-4 bg-muted/30 rounded-lg text-center">
                                <p className="text-2xl font-bold">{formatBytes(totalOriginalSize)}</p>
                                <p className="text-xs text-muted-foreground uppercase tracking-wider">Original</p>
                            </div>
                            <div className="p-4 bg-green-500/10 rounded-lg text-center border-green-500/20 border">
                                <p className="text-2xl font-bold text-green-600">{savedPercent}%</p>
                                <p className="text-xs text-green-600/80 uppercase tracking-wider">Saved</p>
                            </div>
                            <div className="p-4 bg-muted/30 rounded-lg text-center">
                                <p className="text-2xl font-bold">{formatBytes(savedBytes)}</p>
                                <p className="text-xs text-muted-foreground uppercase tracking-wider">Reduced</p>
                            </div>
                        </div>
                    )}



                    {/* Action buttons */}
                    <div className="flex gap-2 flex-wrap mt-4">
                        {/* Placeholder for future retry button if failed */}
                        {batchStatus.status === 'failed' && (
                            <Button variant="outline" onClick={() => refetch()}>
                                Retry Status Check
                            </Button>
                        )}
                    </div>


                </CardContent>
            </Card>

            {/* Image list */}
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                        <ImageIcon className="w-4 h-4" />
                        Optimized Images
                        <span className="ml-auto text-xs font-normal text-muted-foreground bg-muted px-2 py-1 rounded-full">
                            {batchStatus.images.length} items
                        </span>
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                    <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                        {batchStatus.images.map((image) => (
                            <div
                                key={image.id}
                                className="group flex items-center justify-between p-3 hover:bg-muted/50 rounded-lg border border-transparent hover:border-border transition-all"
                            >
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                    <div className="w-8 h-8 rounded bg-muted flex items-center justify-center shrink-0">
                                        <ImageIcon className="h-4 w-4 text-muted-foreground" />
                                    </div>
                                    <div className="flex flex-col min-w-0">
                                        <span className="text-sm font-medium truncate" title={image.source?.file_path || image.source?.filename}>
                                            {image.source?.file_path || image.source?.filename || 'Unknown'}
                                        </span>
                                        <span className="text-xs text-muted-foreground truncate">
                                            {(image.status === 'completed' || image.status === 'success')
                                                ? `${formatBytes(image.originalSizeBytes)} → ${formatBytes(image.optimizedSizeBytes || 0)}`
                                                : formatBytes(image.originalSizeBytes)
                                            }
                                        </span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 shrink-0">
                                    {(image.status === 'completed' || image.status === 'success') && image.optimizedSizeBytes && image.originalSizeBytes > 0 && (
                                        <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded">
                                            -{Math.round((1 - image.optimizedSizeBytes / image.originalSizeBytes) * 100)}%
                                        </span>
                                    )}
                                    <StatusBadge status={image.status} />
                                    {image.downloadUrl && (
                                        <Button asChild size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-foreground">
                                            <a href={image.downloadUrl} download title="Download Image">
                                                <Download className="h-4 w-4" />
                                            </a>
                                        </Button>
                                    )}
                                    {!image.downloadUrl && image.deletedAt && (
                                        <Button size="icon" variant="ghost" disabled className="h-8 w-8 text-muted-foreground/50" title="Expired">
                                            <Download className="h-4 w-4" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        </div >
    );
};


